import { createContext } from 'react';

import { Team, TeamUser, User } from './userTypes';

// eslint-disable-next-line typescript-enum/no-enum
export enum Locales {
  en = 'en',
  es = 'es',
  de = 'de-DE',
  fr = 'fr-FR',
  ja = 'ja-JP',
  pt = 'pt-BR',
  ko = 'ko-KR',
  zh = 'zh-CN',
}

/**
 * A viewer's cart data
 */
export interface Cart {
  itemsCount: number;
  isMobile: boolean;
  isDarkTheme: boolean;
  items: {
    clipId: number;
    id: number;
    quality: string;
    pricingTierId: number;
  }[];
  currencyCode: string;
}

/**
 * A viewer's filters for content viewing preferences
 */
type Filters = Record<string, { rating: number; name: string }>;

/**
 * Model representing the current user, even when logged out.
 */

export interface Viewer {
  /**
   * User response from the Vimeo api for the current user or null if logged out.
   */
  user: User | null;

  /**
   * Update the user response
   */
  setUser?: (user: User) => void;

  /**
   * Force refresh the JWT token
   */
  refreshJwt?: () => Promise<string>;

  /**
   * Info about the current team if one exists
   */
  team?: Team;

  /**
   * ID for this user used for marketing, analytics, and AB tests.
   */
  vuid: string;

  /**
   * JWT token for the Vimeo api.
   */
  jwt: string;

  /**
   * URL of the appropiate API for the  current environment.
   */
  apiUrl: string;

  /**
   * URL of the appropiate viewmaster cdn.
   */
  viewmasterCdnUrl: string;

  /**
   * XSRF token for the Vimeo website.
   */
  xsrft: string;

  /**
   * Recaptcha site key for the Vimeo website.
   */
  recaptchaSiteKey: string;

  /**
   * Country code detected in the server request.
   */
  location: string;

  /**
   * Locale detected in the server request.
   */
  locale: string;

  /**
   * Supported locales
   */
  locales: Locales[];

  /**
   * Supported locales' label
   */
  localeLabels: string[];

  /**
   * ablincolnConfig for the user
   */
  ablincolnConfig: any; // eslint-disable-line @typescript-eslint/no-explicit-any

  /**
   * eppo config for the user
   */
  eppoConfig: any; // eslint-disable-line @typescript-eslint/no-explicit-any

  /**
   * Flag used by ABLincoln to bucket mobile users. This is determined by parsing
   * the viewer's user agent and should be avoided. Prefer client-side feature
   * and viewport detection instead.
   */
  isMobile: boolean;

  /**
   * Flag used to detect whether Vimeo Record Tool can be run in the current web client
   */
  isRecordToolSupported: boolean;

  /**
   * Config data for any items in the global shopping cart
   */
  cart: Cart | null;

  /**
   * Flag used to know if the user is from a GDPR country
   */
  gdprQualifies?: boolean;

  /**
   * Flag used to know if the user is from a OFCOM country
   */
  ofcomQualifies?: boolean;

  /**
   * Flag used to know if the user should have access to the impressum legal document (Germany only)
   */
  impressumQualifies?: boolean;

  /**
   * Whether the marketing opt-in checkbox in the join form is checked by default
   */
  shouldOptIntoMarketing?: boolean;

  /**
   * Source string which is used to render the GDPR opt-in banner via `eval()`.
   */
  gdprBannerJS?: string;

  /**
   * Source string which is used to the vuid cookie on the client via `eval()`.
   */
  vuidCookieJS?: string;

  /**
   * Flag which is true if the user is opted-in to third party tracking.
   */
  thirdPartyTracking: boolean;

  /**
   * The viewing preferences for the user
   */
  contentViewingPrefs: {
    allowContentRatingBypass: boolean;
    selected: number;
    filters: Filters;
    customFilters: Filters;
    contentRatingList: string[];
  };

  /**
   * Is true if the user has decided to opt out of the mobile experience
   */
  optOutMobile: boolean;

  /**
   * Type of colored ribbon displayed in the topnav.
   */
  ribbonModifier?: 'mod' | 'possessed' | 'rainbow';

  /**
   * API key for quering google places api.
   */
  googleMapApiKey: string;

  /**
   * Https url of the current Vimeo environment
   * (i.e - https://vimeo.dev, https://<branch>.ci.vimeows.com, https://vimeo.com)
   */
  vimeoHttpsUrl: string;

  /**
   * Magisto Api host for Vimeo Create
   */
  magistoApiHost: string;

  /**
   * Whether or not to disable omnisearch. If true, takes precedence over omnisearchEnabled
   */
  omnisearchDisabled?: boolean;

  /**
   * Whether or not to enable omnisearch
   */
  omnisearchEnabled?: boolean;

  /**
   * Facebook app id to be used for accessing facebook APIs
   */
  facebookAppId?: string;

  /**
   * Link to donwload Screen Recorder extension from Chrome store
   */
  screenRecorderExtensionLink?: string;
  /**
   * team user options if any
   */
  teamUser?: TeamUser;

  /**
   * flag to show Terminate item on footers - For German paid users only
   */
  terminateContractQualifies?: boolean;

  /**
   * flag to indicate if the user has been bucketed to the per seat pricing model
   */
  isPerSeatPricing?: boolean;

  /**
   * site key for Recaptcha Enterprise
   */
  recaptchaEnterpriseScoreBasedSiteKey: string;

  /**
   * flag to indicate is domain is for China or not
   */
  isSimplifiedSite?: boolean;

  /**
   * flag to indicate is domain is for Enterprise or not
   */
  isEnterpriseSite?: boolean;

  /**
   * flag to indicate should privacy options in terms and conditions be shown
   */
  termsShowPrivacyChoices?: boolean;

  /**
   * Vat info
   */
  vatConfig: {
    countries: { [key: string]: number };
  };

  /**
   * flag to indicate if the request is coming from a copyright restricted region
   */
  isFromCopyrightRestrictedRegion: boolean;

  /**
   * flag to indicate if the user has been bucketed to the Turnstile blocking experiment
   */
  isTurnstileEnabled: boolean;

  /**
   * site key for Cloudflare Turnstile
   */
  turnstileSiteKey: string;

  /**
   * invisible site key for Cloudflare Turnstile
   */
  turnstileSiteKeyInvisible: string;
}

/**
 * The viewer context is meant to pass the entire viewer down through
 * the application. This will:
 *
 *   1. Avoid prop drilling
 *   2. Allow for hooks to be written that depend on this data without
 *      requiring they be explicitly passed down
 *
 * For now we need to presume this data is nullable because it is only
 * available at runtime. Once we can access the viewer properly through
 * the API, this will be non-nullable as the backend will be able to
 * request the same user data and hydrate this context.
 */
export const ViewerContext = createContext<Viewer | null>(null);
